import useSWR from 'swr'
import {
  fetchProductPresentmentPrices,
  LocalProductPrice
} from '@api/graphqlShopify'
import useBrowsingInNativeCurrency from '@helpers/hooks/useBrowsingInNativeCurrency'
import { useCurrency } from '@helpers/context/CurrencyContext'
import { getNativeCurrencyCode } from '@helpers/env'
import trackError from '@helpers/trackError'

type LocalisedPrice = {
  price: number
  fullPrice: number | null
  currency?: string
  isLoading: boolean
}

const pendingBatches = new Map<string, { productIds: Set<number>; prices: Promise<LocalProductPrice[]> }>()

export default function useLocalisedPrice(productId: number, nativePrice: string, nativeCompareAtPrice: string): LocalisedPrice {
  const browsingInNativeCurrency = useBrowsingInNativeCurrency()
  const { selectedCountry } = useCurrency()

  const { data, error, isLoading } = useSWR<LocalisedPrice, Error>(
    browsingInNativeCurrency || !selectedCountry
      ? null
      : [selectedCountry, productId],
    fetchLocalisedPrice,
    {
      revalidateOnFocus: false,
      onError: error => {
        trackError(new Error('Failed to fetch presentment prices'), { error: error as Error, section: 'Currency' })
      }
    }
  )

  const native: LocalisedPrice = {
    price: Number(nativePrice),
    fullPrice: Number(nativeCompareAtPrice),
    currency: getNativeCurrencyCode(),
    isLoading: false
  }

  if (browsingInNativeCurrency || error) return native
  if (!data) return { ...native, isLoading }
  return data
}

async function fetchLocalisedPrice([country, productId]: [string, number]): Promise<LocalisedPrice> {
  const prices = await getBatchedPrices(country, productId)
  const price = prices.find(price => price.productId === productId)

  if (!price) throw new Error(`No presentment price returned for product ${productId}`)

  return localisedPriceFrom(price)
}

function getBatchedPrices(country: string, productId: number): Promise<LocalProductPrice[]> {
  let batch = pendingBatches.get(country)

  if (!batch) {
    const productIds = new Set<number>()
    const prices = new Promise<LocalProductPrice[]>(resolve => {
      setTimeout(() => {
        pendingBatches.delete(country)
        resolve(fetchProductPresentmentPrices([...productIds], country))
      }, 0)
    })

    batch = { productIds, prices }
    pendingBatches.set(country, batch)
  }

  batch.productIds.add(productId)
  return batch.prices
}

function localisedPriceFrom(price: LocalProductPrice): LocalisedPrice {
  return {
    price: price.price,
    fullPrice: price.compareAtPrice,
    currency: price.currencyCode,
    isLoading: false
  }
}
